import { useState } from 'react'

import { Box, Divider } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import dayjs from 'dayjs'

export interface DateRangeValue {
  gte?: Date | null
  lte?: Date | null
}

export interface DatePreset {
  label: string
  value: string
  start: () => Date
  end: () => Date
}

interface DateFilterProps {
  onDateChange: (value: DateRangeValue) => void
  onClose?: () => void
  maxDate?: Date
  allowFutureDates?: boolean
  presets?: DatePreset[]
}

const preSelectedDateRanges = [
  {
    label: 'Last 2 days',
    value: 'last2Days',
    start: () => dayjs().subtract(2, 'day').startOf('day').toDate(),
    end: () => new Date(),
  },
  {
    label: 'Last 7 days',
    value: 'last7Days',
    start: () => dayjs().subtract(7, 'day').startOf('day').toDate(),
    end: () => new Date(),
  },
  {
    label: 'Last 30 days',
    value: 'last30Days',
    start: () => dayjs().subtract(30, 'day').startOf('day').toDate(),
    end: () => new Date(),
  },
  {
    label: 'This month',
    value: 'thisMonth',
    start: () => dayjs().startOf('month').toDate(),
    end: () => new Date(),
  },
  {
    label: 'Last month',
    value: 'lastMonth',
    start: () => dayjs().subtract(1, 'month').startOf('month').toDate(),
    end: () => dayjs().subtract(1, 'month').endOf('month').toDate(),
  },
]

const futureDateRanges = [
  {
    label: 'Today',
    value: 'today',
    start: () => dayjs().startOf('day').toDate(),
    end: () => dayjs().endOf('day').toDate(),
  },
  {
    label: 'Next 7 days',
    value: 'next7Days',
    start: () => dayjs().startOf('day').toDate(),
    end: () => dayjs().add(7, 'day').endOf('day').toDate(),
  },
  {
    label: 'Next 14 days',
    value: 'next14Days',
    start: () => dayjs().startOf('day').toDate(),
    end: () => dayjs().add(14, 'day').endOf('day').toDate(),
  },
  {
    label: 'Next 30 days',
    value: 'next30Days',
    start: () => dayjs().startOf('day').toDate(),
    end: () => dayjs().add(30, 'day').endOf('day').toDate(),
  },
  {
    label: 'This week',
    value: 'thisWeek',
    start: () => dayjs().startOf('week').toDate(),
    end: () => dayjs().endOf('week').toDate(),
  },
  {
    label: 'Next week',
    value: 'nextWeek',
    start: () => dayjs().add(1, 'week').startOf('week').toDate(),
    end: () => dayjs().add(1, 'week').endOf('week').toDate(),
  },
]

const DateFilter = ({
  onDateChange,
  onClose,
  maxDate,
  allowFutureDates = false,
  presets,
}: DateFilterProps) => {
  const [showCalendar, setShowCalendar] = useState(false)

  const handleDateRangeSelect = (start: Date, end: Date) => {
    const startTime = dayjs(start).startOf('day').toDate()
    const endTime = end
      ? dayjs(end).endOf('day').toDate()
      : dayjs(start).endOf('day').toDate()
    onDateChange({ gte: startTime, lte: endTime })
    onClose?.()
  }

  const dateRanges = presets ?? (allowFutureDates ? futureDateRanges : preSelectedDateRanges)

  if (showCalendar) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4">
        <Divider className="w-[85%]" variant="dashed" />
        <DatePicker
          type="range"
          allowSingleDateInRange
          size="sm"
          maxDate={allowFutureDates ? undefined : (maxDate ?? new Date())}
          onChange={(value) => {
            const [start, end] = value
            if (start && end) {
              handleDateRangeSelect(start, end)
              setShowCalendar(false)
              onClose?.()
            }
          }}
        />
      </div>
    )
  }

  return (
    <div>
      <div>
        {dateRanges.map((dateRange) => (
          <Box
            key={dateRange.value}
            className="flex flex-row items-center justify-between rounded p-2 text-xs hover:bg-doubleNickel-gray-100"
            onClick={() => {
              handleDateRangeSelect(dateRange.start(), dateRange.end())
            }}
          >
            {dateRange.label}
          </Box>
        ))}
      </div>
      <Box
        className="flex flex-row items-center justify-between rounded p-2 text-xs hover:bg-doubleNickel-gray-100"
        onClick={() => {
          setShowCalendar(true)
        }}
      >
        Select dates from calendar
      </Box>
    </div>
  )
}

export default DateFilter
